import React from 'react';
import { Shield, Lock, Eye, Database, Server } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { SEO } from '../components/SEO';

export function Privacy() {
  const { language } = useLanguage();

  const content = {
    hero: {
      title: {
        fr: 'Politique de Confidentialité',
        en: 'Privacy Policy'
      },
      subtitle: {
        fr: 'Protection de vos données et respect de votre vie privée',
        en: 'Protection of your data and respect for your privacy'
      },
      lastUpdated: {
        fr: 'Dernière mise à jour : 15 mars 2025',
        en: 'Last updated: March 15, 2025'
      }
    },
    sections: [
      {
        icon: <Eye className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Collecte des Données',
          en: 'Data Collection'
        },
        content: {
          fr: `Nous collectons uniquement les données nécessaires à la fourniture de nos services de sécurité. 
               Cela inclut :
               • Les informations de contact professionnelles
               • Les logs système et données de sécurité
               • Les informations techniques sur votre infrastructure
               • Les données d'utilisation de nos services`,
          en: `We only collect data necessary for providing our security services. 
               This includes:
               • Professional contact information
               • System logs and security data
               • Technical information about your infrastructure
               • Service usage data`
        }
      },
      {
        icon: <Database className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Utilisation des Données',
          en: 'Data Usage'
        },
        content: {
          fr: `Vos données sont utilisées pour :
               • Fournir nos services de sécurité
               • Détecter et prévenir les menaces
               • Améliorer nos services
               • Respecter nos obligations légales
               
               Nous ne vendons jamais vos données à des tiers.`,
          en: `Your data is used for:
               • Providing our security services
               • Detecting and preventing threats
               • Improving our services
               • Complying with legal obligations
               
               We never sell your data to third parties.`
        }
      },
      {
        icon: <Lock className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Protection des Données',
          en: 'Data Protection'
        },
        content: {
          fr: `Nous mettons en œuvre des mesures de sécurité strictes :
               • Chiffrement de bout en bout
               • Contrôles d'accès stricts
               • Surveillance continue
               • Audits de sécurité réguliers
               
               Toutes les données sont stockées dans des centres de données sécurisés en Suisse.`,
          en: `We implement strict security measures:
               • End-to-end encryption
               • Strict access controls
               • Continuous monitoring
               • Regular security audits
               
               All data is stored in secure data centers in Switzerland.`
        }
      },
      {
        icon: <Server className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Conservation des Données',
          en: 'Data Retention'
        },
        content: {
          fr: `Nous conservons vos données :
               • Données de compte : durée du contrat + 12 mois
               • Logs de sécurité : 24 mois
               • Données de facturation : 10 ans (obligation légale)
               
               Vous pouvez demander la suppression de vos données à tout moment.`,
          en: `We retain your data for:
               • Account data: contract duration + 12 months
               • Security logs: 24 months
               • Billing data: 10 years (legal requirement)
               
               You can request deletion of your data at any time.`
        }
      }
    ],
    rights: {
      title: {
        fr: 'Vos Droits',
        en: 'Your Rights'
      },
      items: [
        {
          title: {
            fr: 'Accès',
            en: 'Access'
          },
          description: {
            fr: 'Droit d\'accéder à vos données personnelles',
            en: 'Right to access your personal data'
          }
        },
        {
          title: {
            fr: 'Rectification',
            en: 'Rectification'
          },
          description: {
            fr: 'Droit de corriger vos données inexactes',
            en: 'Right to correct inaccurate data'
          }
        },
        {
          title: {
            fr: 'Suppression',
            en: 'Deletion'
          },
          description: {
            fr: 'Droit de demander la suppression de vos données',
            en: 'Right to request deletion of your data'
          }
        },
        {
          title: {
            fr: 'Portabilité',
            en: 'Portability'
          },
          description: {
            fr: 'Droit de recevoir vos données dans un format structuré',
            en: 'Right to receive your data in a structured format'
          }
        }
      ]
    },
    contact: {
      title: {
        fr: 'Contact',
        en: 'Contact'
      },
      content: {
        fr: `Pour toute question concernant vos données personnelles, contactez notre Délégué à la Protection des Données :
             
             Email: hello@secusoc.io
             Adresse: 53, Avenue Blanc, 1202 Geneva, Switzerland
             Téléphone: +41 22 740 28 30`,
        en: `For any questions regarding your personal data, contact our Data Protection Officer:
             
             Email: hello@secusoc.io
             Address: 53, Avenue Blanc, 1202 Geneva, Switzerland
             Phone: +41 22 740 28 30`
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title={content.hero.title[language]}
        description={content.hero.subtitle[language]}
        keywords={['privacy policy', 'data protection', 'GDPR', 'security']}
      />

      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              {content.hero.subtitle[language]}
            </p>
            <p className="text-gray-400">
              {content.hero.lastUpdated[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto space-y-20">
          {/* Policy Sections */}
          {content.sections.map((section, index) => (
            <div key={index} className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {section.title[language]}
                </h2>
              </div>
              <div className="bg-gray-800/30 rounded-lg p-8">
                <p className="text-gray-300 whitespace-pre-line leading-relaxed">
                  {section.content[language]}
                </p>
              </div>
            </div>
          ))}

          {/* Rights Section */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold text-white text-center">
              {content.rights.title[language]}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {content.rights.items.map((right, index) => (
                <div key={index} className="bg-gray-800/30 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {right.title[language]}
                  </h3>
                  <p className="text-gray-300">
                    {right.description[language]}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white text-center">
              {content.contact.title[language]}
            </h2>
            <div className="bg-gray-800/30 rounded-lg p-8">
              <p className="text-gray-300 whitespace-pre-line leading-relaxed text-center">
                {content.contact.content[language]}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}